import axios from 'axios';
import { useAdminAuthStore } from './admin-auth.store';
import { useAuthStore } from './auth.store';
import { getPlatformToken } from './platform-session';
import { deriveAppTypeFromPath } from './platform';
import { disconnectSocket } from './socket';
import { ADMIN_LOGIN_PATH, isAdminPath } from './admin-paths';
import { isCookieSessionToken } from './session-token';

function getApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return '/api';
  }

  return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';
}

export const api = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
});

// Attach JWT token to every request
api.interceptors.request.use(config => {
  if (typeof window !== 'undefined') {
    const explicitAuthorization = String(config.headers?.Authorization || config.headers?.authorization || '');
    const explicitBearerToken = explicitAuthorization.match(/^Bearer\s+(.+)$/i)?.[1] || null;
    if (isCookieSessionToken(explicitBearerToken)) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
    const hasExplicitAuthorization = Boolean(config.headers?.Authorization || config.headers?.authorization);
    const token = isAdminPath(window.location.pathname)
      ? useAdminAuthStore.getState().token || getPlatformToken()
      : getPlatformToken() || useAdminAuthStore.getState().token;
    if (token && !isCookieSessionToken(token) && !hasExplicitAuthorization) config.headers.Authorization = `Bearer ${token}`;

    const hasExplicitAppType = Boolean(config.headers?.['X-App-Type'] || config.headers?.['x-app-type']);
    const appType = deriveAppTypeFromPath(window.location.pathname);
    if (appType && !hasExplicitAppType) {
      config.headers['X-App-Type'] = appType;
    }
  }
  return config;
});

// Handle expired/invalid sessions globally — but not on auth endpoints themselves.
// 401 = unauthenticated. 403 on an admin page = the admin session lapsed (idle timeout or a
// newer login replaced it), so RolesGuard rejects it; without this the admin pages just render
// blank/half because every admin data call is refused. 403 is treated as a session lapse ONLY on
// admin paths — elsewhere a 403 can be a legitimate permission/subscription error and must not
// force a logout.
api.interceptors.response.use(
  res => res,
  err => {
    const isAuthEndpoint = err.config?.url?.includes('/auth/');
    const hasToken = Boolean(getPlatformToken() || useAdminAuthStore.getState().token);
    const onAdminPath = typeof window !== 'undefined' && isAdminPath(window.location.pathname);
    const status = err.response?.status;
    const sessionLapsed = status === 401 || (status === 403 && onAdminPath);
    if (sessionLapsed && typeof window !== 'undefined' && !isAuthEndpoint && hasToken) {
      localStorage.removeItem('access_token');
      sessionStorage.removeItem('access_token');
      disconnectSocket();
      useAuthStore.getState().logout();
      useAdminAuthStore.getState().logout();
      window.location.href = onAdminPath ? ADMIN_LOGIN_PATH : '/login';
    }
    return Promise.reject(err);
  },
);
